import React from 'react'
import {
  TableContainer,
  TitleContainer,
  Title,
  TableContentWrapper,
  ContentContainer,
  ContentWrapper,
  ArtistWrapper,
  Content,
  MobileContent,
  Row,
  Image,
  PlayIconWrapper,
  ReleaseInfoLink,
  MobileActionContent,
  ReleaseArtworkWrapper,
  ReleaseIdentity
} from './ReleaseList.styles'
import { useDispatch } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import Tooltip from 'components/Tooltip'
import { format } from 'date-fns'
import Badges from '../Badges/Badges'
import { useTranslation } from 'react-i18next'
import { useTheme } from 'styled-components'
import { MaterialIcon, Pill } from '@dsny/dsny-component-library'
import ReleaseMoreActionsDropdown from 'components/Dropdowns/ReleaseMoreActionsDropdown/ReleaseMoreActionsDropdown'
import ReleaseContextMenu from 'components/Dropdowns/ReleaseMoreActionsDropdown/ReleaseContextMenu'
import { formatSeconds } from 'utils/stringHelpers'
import PlaceholderLogo from 'images/icons/placeholder.png'
import {
  useDesktopMediaQuery,
  useMobileMediaQuery,
  useTabletDownMediaQuery,
  useTabletAndAboveMediaQuery
} from 'utils/hooks/useMediaQueries'
import {
  RESPONSIVE_BREAKPOINT_VERSIONS,
  useResponsiveBreakpointsVersion
} from 'contexts/ResponsiveBreakpointsContext'
import { thunks as PlayQueueThunks } from 'reducers/PlayQueue'
import { PLAY_SOURCES, PLAY_TRIGGERS } from 'constants/tracking'
import { formatDateWithTimeZoneFromSeconds } from 'utils/dateHelpers'
import { ActionButton } from 'components/ReleaseCard/ReleaseCard.styles'
import { AIFilled } from '@dsny/dsny-component-library/icons'
import useArchiveToFilter from 'utils/hooks/useArchiveToFilter'
import ArchivedArtworkOverlay from 'components/ArchivedArtworkOverlay/ArchivedArtworkOverlay'

const ReleasesList = ({ listviewreleases }) => {
  const theme = useTheme()
  const { enabled: archiveToFilterEnabled } = useArchiveToFilter()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const responsiveBreakpointsVersion = useResponsiveBreakpointsVersion()
  const responsiveBreakpointsV2 =
    responsiveBreakpointsVersion === RESPONSIVE_BREAKPOINT_VERSIONS.V2
  const isDesktop = useDesktopMediaQuery()
  const isMobile = useMobileMediaQuery()
  const isTabletDown = useTabletDownMediaQuery()
  const isTabletAndAbove = useTabletAndAboveMediaQuery()
  const { t } = useTranslation()

  const handlePlayRelease = (release) => {
    dispatch(
      PlayQueueThunks.playRelease(release.releaseid, {
        source: PLAY_SOURCES.RELEASES,
        trigger: PLAY_TRIGGERS.ROW
      })
    )
  }

  const handleReleaseClick = (release) => {
    navigate(`/releases/releaseInfo/${release.releaseid}`)
  }

  const handleReleaseLinkClick = (event, release) => {
    event.preventDefault()
    event.stopPropagation()
    handleReleaseClick(release)
  }

  return (
    <TableContainer>
      {isTabletAndAbove && (
        <TitleContainer $responsiveBreakpointsV2={responsiveBreakpointsV2}>
          <Title>{t('grid-list.artist-and-title')}</Title>
          <Title>{t('grid-list.length')}</Title>
          <Title>{t('grid-list.available-date')}</Title>
          <Title>{t('grid-list.impact-date')}</Title>
          {isDesktop && (
            <>
              <Title>{t('grid-list.label')}</Title>
              <Title>{t('grid-list.genres')}</Title>
            </>
          )}
          <Title></Title>
          <Title></Title>
        </TitleContainer>
      )}
      <tbody>
        <TableContentWrapper $isMobile={isMobile}>
          {listviewreleases.map((release, index) => {
            const formattedAvailableDate = format(
              new Date(release?.available),
              'MMM dd, yyyy'
            )
            const releaseInfoPath = `/releases/releaseInfo/${release.releaseid}`

            return (
              <ReleaseContextMenu
                key={`${release?.id}-${index}`}
                release={release}
              >
                <ContentContainer
                  $responsiveBreakpointsV2={responsiveBreakpointsV2}
                  data-cy="release-row"
                  data-release-id={release?.releaseid}
                  onClick={() => handleReleaseClick(release)}
                >
                  <ReleaseIdentity>
                    <ReleaseArtworkWrapper
                      $responsiveBreakpointsV2={responsiveBreakpointsV2}
                    >
                      <Image
                        $isDesktop={isDesktop}
                        $isMobile={isMobile}
                        $responsiveBreakpointsV2={responsiveBreakpointsV2}
                        src={release?.thumbnail || PlaceholderLogo}
                        alt="Album"
                      />
                      {archiveToFilterEnabled && release.archived && (
                        <ArchivedArtworkOverlay small />
                      )}
                      {!isTabletDown && (
                        <PlayIconWrapper className="action_btn">
                          <Tooltip
                            hasArrow={false}
                            placement="top"
                            text={t('release-list.play-release')}
                          >
                            <ActionButton
                              $responsiveBreakpointsV2={responsiveBreakpointsV2}
                              variant="primary"
                              setting="player"
                              mode="dark"
                              onClick={(e) => {
                                e.stopPropagation()
                                handlePlayRelease(release)
                              }}
                            >
                              <MaterialIcon
                                name="PlayArrow"
                                color={theme.colors.black0}
                              />
                            </ActionButton>
                          </Tooltip>
                        </PlayIconWrapper>
                      )}
                    </ReleaseArtworkWrapper>

                    {!isTabletDown && (
                      <>
                        <Row>
                          <MobileContent
                            $responsiveBreakpointsV2={responsiveBreakpointsV2}
                          >
                            <MobileContent
                              $responsiveBreakpointsV2={responsiveBreakpointsV2}
                              style={{
                                marginBottom: 2
                              }}
                            >
                              <ReleaseInfoLink
                                to={releaseInfoPath}
                                onClick={(event) =>
                                  handleReleaseLinkClick(event, release)
                                }
                              >
                                {release?.title}
                              </ReleaseInfoLink>
                            </MobileContent>

                            <ArtistWrapper
                              $responsiveBreakpointsV2={responsiveBreakpointsV2}
                            >
                              {release?.explicit && (
                                <MaterialIcon
                                  name="Explicit"
                                  color={theme.colors.neutralW100}
                                />
                              )}
                              {release?.is_ai_generated && (
                                <AIFilled
                                  width={16}
                                  height={16}
                                  color={theme.colors.neutralW100}
                                />
                              )}
                              <ReleaseInfoLink
                                to={releaseInfoPath}
                                onClick={(event) =>
                                  handleReleaseLinkClick(event, release)
                                }
                              >
                                {release?.artist}
                              </ReleaseInfoLink>
                            </ArtistWrapper>
                          </MobileContent>
                        </Row>
                      </>
                    )}

                    {isTabletDown && (
                      <ContentWrapper>
                        <MobileContent
                          $isTitle
                          $responsiveBreakpointsV2={responsiveBreakpointsV2}
                        >
                          <ReleaseInfoLink
                            to={releaseInfoPath}
                            onClick={(event) =>
                              handleReleaseLinkClick(event, release)
                            }
                          >
                            {release?.title}
                          </ReleaseInfoLink>
                        </MobileContent>

                        <MobileContent
                          $responsiveBreakpointsV2={responsiveBreakpointsV2}
                        >
                          <ArtistWrapper
                            $responsiveBreakpointsV2={responsiveBreakpointsV2}
                          >
                            {release?.explicit && (
                              <MaterialIcon
                                name="Explicit"
                                color={theme.colors.neutralW100}
                              />
                            )}
                            {release?.is_ai_generated && (
                              <AIFilled
                                width={16}
                                height={16}
                                color={theme.colors.neutralW100}
                              />
                            )}
                            <ReleaseInfoLink
                              to={releaseInfoPath}
                              onClick={(event) =>
                                handleReleaseLinkClick(event, release)
                              }
                            >
                              {release?.artist}
                            </ReleaseInfoLink>
                          </ArtistWrapper>
                        </MobileContent>
                      </ContentWrapper>
                    )}
                  </ReleaseIdentity>

                  {isMobile ? (
                    <MobileActionContent
                      onClick={(event) => event.stopPropagation()}
                    >
                      <ReleaseMoreActionsDropdown listReleases={release} />
                    </MobileActionContent>
                  ) : (
                    <Content>{`${formatSeconds(release?.duration)}`}</Content>
                  )}

                  {isTabletAndAbove && (
                    <>
                      <Content>{formattedAvailableDate}</Content>

                      <Content>
                        {release?.impactdate
                          ? formatDateWithTimeZoneFromSeconds(
                              release?.impactdate
                            )
                          : formattedAvailableDate}
                      </Content>

                      {isDesktop && (
                        <>
                          <Content>
                            {release?.labels &&
                              release?.labels.map((label, index) => (
                                <span key={`${label}-${index}`}>
                                  {label.name}
                                  {index < release.labels.length - 1 ? ',' : ''}
                                </span>
                              ))}
                          </Content>
                          <Content>
                            {release?.genres &&
                              release?.genres?.map((genre, index) => (
                                <span key={`${genre}-${index}`}>
                                  {genre}
                                  {index < release.genres.length - 1 ? ',' : ''}
                                </span>
                              ))}
                          </Content>
                        </>
                      )}

                      <Content>
                        <Badges
                          release={release}
                          style={{ flexWrap: 'wrap', gap: 4, padding: '8px 0' }}
                        />
                        {release?.markettags?.includes('cancon') && (
                          <Pill
                            variant="tonal"
                            mode="dark"
                            label={t('release-list.canadian')}
                            icon={<MaterialIcon name="CanadaFlag" />}
                            style={{
                              textTransform: 'uppercase',
                              width: 'auto',
                              maxWidth: '100px',
                              backgroundColor: theme.colors.neutralW30,
                              color: theme.colors.neutralW90
                            }}
                          />
                        )}
                      </Content>

                      <Content
                        $isActionColumn
                        onClick={(e) => e.stopPropagation()}
                      >
                        <ReleaseMoreActionsDropdown listReleases={release} />
                      </Content>
                    </>
                  )}
                </ContentContainer>
              </ReleaseContextMenu>
            )
          })}
        </TableContentWrapper>
      </tbody>
    </TableContainer>
  )
}

export default ReleasesList
